import React, { useState } from 'react';
import { ChevronDown, ShoppingCart } from 'lucide-react';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';

interface WelcomeSectionProps {
  hasPurchased: boolean;
}

export default function WelcomeSection({ hasPurchased }: WelcomeSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          The 21 Day Challenge
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        hasPurchased ? (
          <div className="px-4 pb-8 sm:px-6 space-y-5 text-gray-700 leading-relaxed">
            <p className="text-base font-semibold text-[#004060]">
              Welcome to the 21 Day Unstoppable Challenge.
            </p>
            <p>
              You just made a decision that most people never make. You chose to stop staying stuck.
            </p>

            <div className="space-y-2">
              <p className="font-semibold text-[#004060]">First things first. What is your challenge?</p>
              <p>
                Before your first email arrives, take a moment to identify the one goal or challenge you are bringing into the next 21 days.
              </p>
              <p>
                It can be personal or professional. It can be something you have been avoiding for weeks or something you have been chipping away at for months. The only rule is that it has to matter to you.
              </p>
              <p>
                Pick your most pressing goal. The one that keeps coming up. The one where progress would change everything.
              </p>
              <p className="font-medium text-[#004060]">That is your challenge for the next 21 days.</p>
            </div>

            <p>
              You might be thinking that running six questions against one goal every day does not sound like much. And honestly, that is fair. But here is what we have seen happen when people actually do it. By elevating your core values in that one area every single day something shifts. Not just in your results but in how you see yourself in relation to that goal. That shift is what unstoppable actually feels like.
            </p>
            <p className="font-medium text-[#004060]">Trust the process for 21 days and you will understand what we mean.</p>

            <div className="space-y-2">
              <p className="font-semibold text-[#004060]">Here is what happens next.</p>
              <p>
                Check your inbox. Your first email arrives today with your day one Refinement System questions and your one action to take. Each morning for the next 21 days a new email arrives to keep you moving.
              </p>
              <p>
                On day seven your values based AI prompt unlocks. By then you will have a full week of the Refinement System working for you and you will know exactly how to use this bonus tool to go even deeper.
              </p>
            </div>

            <p>
              That is it. No app to figure out. No dashboard to navigate. Just open your email each morning and do the thing.
            </p>

            <div className="pt-2 border-t border-gray-100 space-y-3">
              <p className="font-semibold text-[#004060]">One last thing before you go.</p>
              <p>
                If you want to fast track your results and work through your challenge with Joey directly, grab a free Alignment Call. A lot of people find that one conversation changes everything.
              </p>
              <a
                href="https://certaingrowth.com/alignmentsession/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#004060] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#236caf] transition-colors duration-200"
              >
                Book Your Alignment Call
              </a>
            </div>
          </div>
        ) : (
          <div className="px-4 pb-8 sm:px-6 space-y-5 text-gray-700 leading-relaxed">
            <p className="text-base font-semibold text-[#004060]">
              Ready to go all in?
            </p>
            <p>
              The 21 Day Unstoppable Challenge takes your core values off the page and puts them to work against your most pressing goal — for 21 days straight.
            </p>
            <p>
              Every morning you get a fresh set of Refinement System questions tailored to your values. By the end of three weeks, you will have built the kind of momentum that sticks.
            </p>
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={() => navigate('/pricing')}
                className="flex items-center gap-2 bg-[#318231] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#236caf] transition-colors duration-200"
              >
                <ShoppingCart className="w-5 h-5" />
                Join the 21 Day Challenge
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
